import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ImageViewer } from '../../design/ImageViewer'
import { Button, Spinner, StarRating, softBadgeColors } from '@structural-climbing/ui'
import { type Discipline, gradeDisplay } from '@structural-climbing/validation'
import { useGymGradeConfig } from '../gym/useGymGradeConfig'
import { MIN_RATINGS_TO_SHOW, type GymRoute } from './useGymRoutes'
import { Sheet } from '../../design/Sheet'
import { invalidateAfterAscent } from './invalidateAfterAscent'
import { RouteCommentField } from './RouteCommentField'

interface RouteClimber {
  userId: string
  nickname: string | null
  name: string | null
  avatarUrl: string | null
  totalAscents: number
  isFirstAscent: boolean
}

interface ClimbersData {
  total: number
  climbers: RouteClimber[]
  private_count: number
  /** The route's live rating aggregate, recomputed server-side on every read. */
  avg_rating?: number | null
  rating_count?: number
  /** The caller's own note to the setters on this route, if they left one. */
  my_comment?: string | null
}

function isNewRoute(createdAt: string): boolean {
  return Date.now() - new Date(createdAt).getTime() < 3 * 24 * 60 * 60 * 1000
}

// Flash, 2, 3, and "3+" — 4 stands for "four attempts or more".
const ATTEMPT_OPTIONS = [1, 2, 3, 4]

function attemptLabel(n: number, flashLabel: string): string {
  if (n === 1) return flashLabel
  if (n === 4) return '3+'
  return String(n)
}

function getInitials(s: string): string {
  return s
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')
}

interface Props {
  route: GymRoute | null
  gymId: string
  userId: string
  apiUrl: string
  discipline?: Discipline
  getAccessTokenSilently: () => Promise<string>
  onClose: () => void
  onLogAscent: (routeId: string, attempts: number | null, rating: number | null) => void
  onRemoveAscent: (routeId: string) => void
  onUpdateAscent: (routeId: string, attempts: number | null, rating: number | null) => void
  /**
   * The route's average moved. Fired after a rating write, with the server's own
   * figure rather than a client estimate, so the sheet and the list row behind it
   * both settle on the same number.
   */
  onRatingRefresh?: (routeId: string, avgRating: number | null, ratingCount: number) => void
  isArchived?: boolean
  canLogAscent?: boolean
  /**
   * Why logging is closed, when it is. Defaults to the gym Routes reason; a
   * competition has its own, and saying "you need an active plan" there is
   * simply untrue.
   */
  cannotLogReason?: string
  /**
   * false hides the "Who climbed this" section and skips its fetch entirely.
   * Competitions have no public climbers list.
   */
  showClimbers?: boolean
  /**
   * false hides every star: the picker on log, the average, and the stars in the
   * edit sheet. Competitions set this. Ratings live on `ascents` and a
   * competition writes `competition_ascents`, which has no rating column, so a
   * star shown here would post a field the endpoint cannot store.
   */
  showRating?: boolean
  /**
   * false stops asking how many attempts it took. A competition scored on
   * routes topped sets this: flashes and attempts do not count there, so the
   * question is noise, and a tap on "Log ascent" should simply log it.
   */
  askAttempts?: boolean
  /**
   * false stops offering a note to the setters on the first log. A competition
   * sets this: its routes are scored, not set for feedback, and its endpoints
   * have nowhere to store one.
   */
  askComment?: boolean
  /**
   * false removes "Log ascent again" once the route is topped. A competition
   * sets this: it keeps one ascent per route, so a second log scores nothing.
   */
  allowRepeat?: boolean
  /**
   * Where ascents are written. Defaults to the gym endpoints; a competition
   * points these at its own, so a competition log never reaches `ascents`.
   */
  ascentEndpoints?: { create: string; item: (routeId: string) => string }
  /**
   * What to say when the server refuses a write, keyed by the `code` in its
   * error body. Anything not listed falls back to the generic "could not save".
   */
  saveErrorMessages?: Record<string, string>
  /** A write was refused with one of the codes above. */
  onSaveRefused?: (code: string) => void
  hasPrev: boolean
  hasNext: boolean
  nextLoading: boolean
  onPrev: () => void
  onNext: () => void
}

export function RouteDetailSheet({
  route,
  gymId,
  userId,
  apiUrl,
  discipline = 'boulder',
  getAccessTokenSilently,
  onClose,
  onLogAscent,
  onRemoveAscent,
  onUpdateAscent,
  onRatingRefresh,
  isArchived = false,
  canLogAscent = true,
  cannotLogReason,
  showClimbers = true,
  showRating = true,
  askAttempts = true,
  askComment = true,
  allowRepeat = true,
  ascentEndpoints,
  saveErrorMessages,
  onSaveRefused,
  hasPrev,
  hasNext,
  nextLoading,
  onPrev,
  onNext,
}: Props) {
  // Gym endpoints by default; a competition supplies its own so its logs land
  // in competition_ascents and never in `ascents`.
  const endpoints = ascentEndpoints ?? {
    create: `${apiUrl}/my-ascents`,
    item: (routeId: string) => `${apiUrl}/my-ascents/${routeId}`,
  }
  const { t, i18n } = useTranslation()
  const gradeConfig = useGymGradeConfig()
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [loggedFeedback, setLoggedFeedback] = useState(false)
  const [attempts, setAttempts] = useState<number | null>(null)
  const [rating, setRating] = useState<number | null>(null)
  const [comment, setComment] = useState('')
  const [commentSent, setCommentSent] = useState(false)
  const [attemptsModalOpen, setAttemptsModalOpen] = useState(false)
  const [confirmLogAgainOpen, setConfirmLogAgainOpen] = useState(false)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [editAttemptsOpen, setEditAttemptsOpen] = useState(false)
  const [pendingEditAttempts, setPendingEditAttempts] = useState<number | null>(null)
  const [pendingEditRating, setPendingEditRating] = useState<number | null>(null)
  const [pendingEditComment, setPendingEditComment] = useState('')
  // Whether the climber has typed in the edit sheet's note yet. Until they do,
  // the box follows their saved note, so opening Edit before it has loaded
  // cannot show an empty box that a save would then write back as "no note".
  const editCommentTouched = useRef(false)
  const [zoomOpen, setZoomOpen] = useState(false)
  const [climbersData, setClimbersData] = useState<ClimbersData | null>(null)
  const [climbersLoading, setClimbersLoading] = useState(false)
  const [climbersExpanded, setClimbersExpanded] = useState(false)
  const [climbersVersion, setClimbersVersion] = useState(0)

  useEffect(() => {
    if (editAttemptsOpen && !editCommentTouched.current)
      setPendingEditComment(climbersData?.my_comment ?? '')
  }, [editAttemptsOpen, climbersData])
  const feedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const swipeStartXRef = useRef(0)
  const swipeStartYRef = useRef(0)

  const loadClimbers = useCallback(async () => {
    // No request at all in competition mode, not just a hidden result.
    if (!route || !showClimbers) return
    setClimbersLoading(true)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(
        `${apiUrl}/my-gym-routes/${route.id}/climbers?gym_id=${encodeURIComponent(gymId)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (res.ok) {
        const data = (await res.json()) as ClimbersData
        setClimbersData(data)
        // Only when it actually moved: this also runs on open, and an
        // unconditional call would rewrite the page's route list on every
        // sheet that gets looked at.
        const avg = data.avg_rating ?? null
        const count = data.rating_count ?? 0
        if (avg !== (route.avg_rating ?? null) || count !== (route.rating_count ?? 0)) {
          onRatingRefresh?.(route.id, avg, count)
        }
      }
    } catch {
      // silently ignore
    } finally {
      setClimbersLoading(false)
    }
  }, [route?.id, gymId, apiUrl, showClimbers, getAccessTokenSilently]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!route) {
      setClimbersData(null)
      setClimbersExpanded(false)
      return
    }
    setClimbersExpanded(false)
    setAttempts(null)
    setRating(null)
    setComment('')
    setAttemptsModalOpen(false)
    void loadClimbers()
  }, [route?.id, climbersVersion]) // eslint-disable-line react-hooks/exhaustive-deps

  // Clean up feedback timer on unmount
  useEffect(() => {
    return () => {
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current)
    }
  }, [])

  if (!route) return null

  // A competition route arrives here without a grade (competition routes are
  // numbered, not graded), even though GymRoute types it as always present.
  const graded = (route.grade as number | null) !== null
  const grade = gradeDisplay(gradeConfig, route.grade, discipline)
  const gradeBadge = softBadgeColors(grade.color)
  const isNew = isNewRoute(route.created_at)
  const dateLabel = new Date(route.created_at).toLocaleDateString(i18n.language, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })

  function handlePhotoTouchStart(e: React.TouchEvent) {
    swipeStartXRef.current = e.touches[0].clientX
    swipeStartYRef.current = e.touches[0].clientY
  }

  function handlePhotoTouchEnd(e: React.TouchEvent) {
    const dx = e.changedTouches[0].clientX - swipeStartXRef.current
    const dy = e.changedTouches[0].clientY - swipeStartYRef.current
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.5) return
    if (dx < 0) onNext()
    else onPrev()
  }

  function showFeedback(sentToSetters = false) {
    setCommentSent(sentToSetters)
    setLoggedFeedback(true)
    if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current)
    // A longer line when the note went through, so there is time to read it.
    feedbackTimerRef.current = setTimeout(
      () => setLoggedFeedback(false),
      sentToSetters ? 3000 : 1500,
    )
  }

  // A refusal the caller can explain (a competition group that has ended, say)
  // is shown in its own words; everything else stays "could not save".
  async function reportRefusal(resp: Response) {
    const body = (await resp.json().catch(() => ({}))) as { code?: string }
    const message = body.code ? saveErrorMessages?.[body.code] : undefined
    setSaveError(message ?? t('routes.saveFailed'))
    if (message && body.code) onSaveRefused?.(body.code)
  }

  async function handleLog() {
    if (!route) return
    setSaving(true)
    setSaveError(null)
    try {
      const token = await getAccessTokenSilently()
      const payload: { route_id: string; attempts?: number; rating?: number; comment?: string } = {
        route_id: route.id,
      }
      if (!route.has_ascent && attempts !== null) payload.attempts = attempts
      // Unlike attempts, a rating rides along on repeat logs too: attempts
      // describe the first send and never change, but a re-log is a fresh
      // opinion about the route.
      if (showRating && rating !== null) payload.rating = rating
      // A note to the setters is asked for on the first log only.
      if (askComment && !route.has_ascent && comment.trim()) payload.comment = comment.trim()
      const resp = await fetch(endpoints.create, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!resp.ok) {
        await reportRefusal(resp)
        return
      }
      const logged = (await resp.json().catch(() => ({}))) as { comment_saved?: boolean }
      invalidateAfterAscent()
      onLogAscent(route.id, !route.has_ascent ? attempts : null, rating)
      setAttemptsModalOpen(false)
      setAttempts(null)
      setRating(null)
      setComment('')
      setClimbersVersion((v) => v + 1)
      setClimbersExpanded(true)
      showFeedback(logged.comment_saved === true)
    } catch {
      setSaveError(t('routes.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  async function handleRemove() {
    if (!route) return
    setSaving(true)
    setSaveError(null)
    try {
      const token = await getAccessTokenSilently()
      const resp = await fetch(endpoints.item(route.id), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!resp.ok) {
        await reportRefusal(resp)
        return
      }
      invalidateAfterAscent()
      onRemoveAscent(route.id)
      setClimbersVersion((v) => v + 1)
    } catch {
      setSaveError(t('routes.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  async function handleUpdateAscentDetails() {
    if (!route) return
    setSaving(true)
    setSaveError(null)
    try {
      const token = await getAccessTokenSilently()
      // Competitions omit rating entirely rather than sending null, which would
      // otherwise clear a rating on an endpoint that has no such column.
      const patch: { attempts: number | null; rating?: number | null; comment?: string | null } = {
        attempts: pendingEditAttempts,
      }
      if (showRating) patch.rating = pendingEditRating
      // Sent only when it changed, so saving new stars never rewrites the note.
      const nextComment = pendingEditComment.trim()
      if (askComment && nextComment !== (climbersData?.my_comment ?? '')) {
        patch.comment = nextComment || null
      }
      const resp = await fetch(endpoints.item(route.id), {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      })
      if (!resp.ok) {
        await reportRefusal(resp)
        return
      }
      invalidateAfterAscent()
      onUpdateAscent(route.id, pendingEditAttempts, pendingEditRating)
      // Re-read the route's aggregate: an edited rating moves the average.
      setClimbersVersion((v) => v + 1)
      setEditAttemptsOpen(false)
      setPendingEditAttempts(null)
      setPendingEditRating(null)
      setPendingEditComment('')
    } catch {
      setSaveError(t('routes.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Sheet
        isOpen
        onClose={onClose}
        data-testid="route-sheet"
        aria-label={t('routes.detailTitle')}
        handleAction={
          <button
            type="button"
            className="rounded-full p-1.5 text-foreground/40 hover:bg-content2 hover:text-foreground active:bg-content3"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="h-6 w-6"
            >
              <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
            </svg>
          </button>
        }
      >
        <div className="min-h-0 flex-1 overflow-y-auto pb-8">
          {/* Route photo */}
          <div
            data-testid="route-photo"
            className="relative"
            onTouchStart={handlePhotoTouchStart}
            onTouchEnd={handlePhotoTouchEnd}
          >
            {route.image_r2_key ? (
              <button
                type="button"
                className="w-full focus:outline-none"
                onClick={() => setZoomOpen(true)}
                aria-label={t('routes.viewFullImage')}
              >
                <img
                  src={`${apiUrl}/climbing-routes/${route.id}/image`}
                  alt={graded ? t('routes.gradeBadge', { grade: route.grade }) : ''}
                  className="max-h-[30vh] w-full object-contain"
                />
              </button>
            ) : (
              <div className="flex h-40 w-full items-center justify-center bg-content2 text-5xl text-foreground/20">
                ⛰
              </div>
            )}

            {hasPrev && (
              <button
                type="button"
                className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white"
                onClick={onPrev}
                aria-label={t('routes.previousRoute')}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="h-5 w-5 rotate-90"
                >
                  <path d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" />
                </svg>
              </button>
            )}

            {hasNext && (
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white disabled:opacity-70"
                onClick={onNext}
                disabled={nextLoading}
                aria-label={t('routes.nextRoute')}
              >
                {nextLoading ? (
                  <Spinner size="sm" color="white" />
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="h-5 w-5 -rotate-90"
                  >
                    <path d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" />
                  </svg>
                )}
              </button>
            )}
          </div>

          <div className="px-6 pt-5">
            {/* Grade + color + badges */}
            <div className="mb-1 flex items-center gap-2">
              {graded && (
                <span
                  className="rounded px-2.5 py-1 text-sm font-semibold"
                  style={{ backgroundColor: gradeBadge.bg, color: gradeBadge.fg }}
                >
                  {t('routes.gradeBadge', { grade: grade.label })}
                </span>
              )}
              <span
                className="h-4 w-4 rounded-full border border-foreground/10"
                style={{ backgroundColor: route.color ?? '#9ca3af' }}
              />
              {isNew && (
                <span className="rounded bg-success px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none text-white">
                  {t('routes.new')}
                </span>
              )}
            </div>

            {route.section && (
              <span className="mb-2 inline-block rounded bg-content2 px-2 py-0.5 text-xs text-foreground/60">
                {route.section.name}
              </span>
            )}

            <p className="mb-4 text-sm text-foreground/50">
              {t('routes.dateSet', { date: dateLabel })}
              {route.setter?.name && (
                <span data-testid="route-setter">
                  {' · '}
                  {t('routes.setBy', { name: route.setter.name })}
                </span>
              )}
            </p>

            {route.setter_note && (
              <div className="mb-4 rounded-lg bg-content2 p-3">
                <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-foreground/60">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="h-4 w-4"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 2c-4.418 0-8 2.91-8 6.5 0 1.84.94 3.5 2.454 4.68a.5.5 0 0 1 .186.457l-.19 1.6a.5.5 0 0 0 .72.5l2.2-1.16a.5.5 0 0 1 .35-.05A9.6 9.6 0 0 0 10 15c4.418 0 8-2.91 8-6.5S14.418 2 10 2Z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {t('routes.setterNoteTitle')}
                </p>
                <p className="whitespace-pre-line text-sm text-foreground/80">
                  {route.setter_note}
                </p>
              </div>
            )}

            {/* Ascent status */}
            <div className="mb-4 flex items-center gap-2">
              <p className="text-sm font-medium">
                {route.has_ascent ? t('routes.youClimbed') : t('routes.notClimbedYet')}
              </p>
              {askAttempts && route.has_ascent && route.attempts === 1 && (
                <span className="rounded bg-warning/15 px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none text-warning-700">
                  {t('routes.flashedBadge')}
                </span>
              )}
            </div>

            {showRating && (
              <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2">
                <div>
                  <p className="mb-0.5 text-xs text-foreground/50">{t('routes.avgRating')}</p>
                  {route.avg_rating != null ? (
                    <StarRating
                      value={route.avg_rating}
                      showValue
                      size="sm"
                      countLabel={t('routes.ratingCount', { count: route.rating_count })}
                      label={t('routes.ratingOutOf', { value: route.avg_rating.toFixed(1) })}
                    />
                  ) : (
                    // The Worker holds the average back until enough climbers
                    // have rated the route. Say so, rather than leave members
                    // wondering where it went.
                    <p data-testid="rating-pending" className="text-xs text-foreground/60">
                      {route.rating_count
                        ? t('routes.ratingPending', {
                            count: route.rating_count,
                            min: MIN_RATINGS_TO_SHOW,
                          })
                        : t('routes.ratingPendingNone', { min: MIN_RATINGS_TO_SHOW })}
                    </p>
                  )}
                </div>
                {route.my_rating != null && (
                  <div>
                    <p className="mb-0.5 text-xs text-foreground/50">{t('routes.yourRating')}</p>
                    <StarRating
                      value={route.my_rating}
                      size="sm"
                      label={t('routes.ratingOutOf', { value: route.my_rating })}
                    />
                  </div>
                )}
              </div>
            )}

            {isArchived ? (
              <p className="text-center text-sm text-foreground/40">{t('routes.archivedNote')}</p>
            ) : !canLogAscent ? (
              <p className="text-center text-sm text-foreground/40">
                {cannotLogReason ?? t('routes.noSubscriptionNote')}
              </p>
            ) : route.has_ascent ? (
              <>
                <div className={`flex gap-2 ${allowRepeat ? '' : 'justify-end'}`}>
                  {allowRepeat && (
                    <Button
                      className="flex-1"
                      color="primary"
                      variant="solid"
                      isLoading={saving}
                      onClick={() => setConfirmLogAgainOpen(true)}
                    >
                      {t('routes.logAgain')}
                    </Button>
                  )}
                  {(askAttempts || showRating || askComment) && (
                    <Button
                      isIconOnly
                      color="default"
                      variant="light"
                      onClick={() => {
                        setPendingEditAttempts(route.attempts)
                        setPendingEditRating(route.my_rating ?? null)
                        setPendingEditComment(climbersData?.my_comment ?? '')
                        editCommentTouched.current = false
                        setEditAttemptsOpen(true)
                      }}
                      aria-label={showRating ? t('routes.editAscent') : t('routes.editAttempts')}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        className="h-5 w-5"
                      >
                        <path d="M2.695 14.763l-1.262 3.154a.5.5 0 0 0 .65.65l3.155-1.262a4 4 0 0 0 1.343-.885L17.5 5.5a2.121 2.121 0 0 0-3-3L3.58 13.42a4 4 0 0 0-.885 1.343Z" />
                      </svg>
                    </Button>
                  )}
                  <Button
                    isIconOnly
                    color="danger"
                    variant="light"
                    onClick={() => setConfirmDeleteOpen(true)}
                    aria-label={t('routes.removeAscent')}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="h-5 w-5"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.75 1A2.75 2.75 0 0 0 6 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 1 0 .23 1.482l.149-.022.841 10.518A2.75 2.75 0 0 0 7.596 19h4.807a2.75 2.75 0 0 0 2.742-2.53l.841-10.52.149.023a.75.75 0 0 0 .23-1.482A41.03 41.03 0 0 0 14 4.193V3.75A2.75 2.75 0 0 0 11.25 1h-2.5ZM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4ZM8.58 7.72a.75.75 0 0 0-1.5.06l.3 7.5a.75.75 0 1 0 1.5-.06l-.3-7.5Zm4.34.06a.75.75 0 1 0-1.5-.06l-.3 7.5a.75.75 0 1 0 1.5.06l.3-7.5Z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </Button>
                </div>
                {loggedFeedback && (
                  <p className="mt-2 text-center text-sm text-success">
                    {commentSent ? t('routes.commentSentToSetters') : t('routes.loggedSuccess')}
                  </p>
                )}
                {saveError && <p className="mt-2 text-center text-sm text-danger">{saveError}</p>}
              </>
            ) : (
              <>
                <Button
                  className="w-full"
                  color="primary"
                  variant="solid"
                  isLoading={saving}
                  // Nothing to ask (no attempts, no stars): log on the tap.
                  onClick={() =>
                    askAttempts || showRating || askComment
                      ? setAttemptsModalOpen(true)
                      : void handleLog()
                  }
                >
                  {t('routes.logAscent')}
                </Button>
                {loggedFeedback && (
                  <p className="mt-2 text-center text-sm text-success">
                    {commentSent ? t('routes.commentSentToSetters') : t('routes.loggedSuccess')}
                  </p>
                )}
                {saveError && <p className="mt-2 text-center text-sm text-danger">{saveError}</p>}
              </>
            )}

            {/* Who climbed this — collapsible. Hidden in competition mode:
                who topped a competition route belongs to the competition,
                not to the gym wall. */}
            {showClimbers && (
              <div className="mt-6 border-t border-divider pt-1">
                <button
                  type="button"
                  className="flex w-full items-center justify-between py-4 text-left"
                  onClick={() => setClimbersExpanded((v) => !v)}
                  aria-expanded={climbersExpanded}
                >
                  <span className="text-base font-bold">{t('routes.whoClimbed')}</span>
                  <span className="flex items-center gap-2">
                    {climbersData && climbersData.total > 0 && (
                      <span className="text-sm text-foreground/50">{climbersData.total}</span>
                    )}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className={`h-5 w-5 text-foreground/40 transition-transform duration-200 ${climbersExpanded ? 'rotate-180' : ''}`}
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </span>
                </button>

                {climbersExpanded && (
                  <div className="max-h-64 overflow-y-auto pb-2">
                    {climbersLoading ? (
                      <div className="flex justify-center py-6">
                        <Spinner size="sm" color="primary" />
                      </div>
                    ) : !climbersData || climbersData.climbers.length === 0 ? (
                      <>
                        <p className="py-4 text-center text-sm text-foreground/40">
                          {t('routes.noPublicClimbers')}
                        </p>
                        {climbersData && climbersData.private_count > 0 && (
                          <p className="text-xs italic text-foreground/40">
                            {t('routes.privateAccountsNote', { count: climbersData.private_count })}
                          </p>
                        )}
                      </>
                    ) : (
                      <div className="flex flex-col gap-3">
                        {climbersData.climbers.map((c, index) => (
                          <Fragment key={c.userId}>
                            <div className="flex items-center gap-3">
                              {c.avatarUrl ? (
                                <img
                                  src={c.avatarUrl}
                                  className="h-8 w-8 shrink-0 rounded-full object-cover"
                                  alt=""
                                />
                              ) : (
                                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-content2 text-xs font-bold text-foreground/50">
                                  {(() => {
                                    const dn = c.nickname ?? c.name
                                    return dn ? getInitials(dn) : '?'
                                  })()}
                                </div>
                              )}
                              <div className="flex-1">
                                <p className="flex items-center gap-1 text-sm font-medium">
                                  {c.isFirstAscent && (
                                    <span
                                      title={t('routes.firstAscentBadge')}
                                      aria-label={t('routes.firstAscentBadge')}
                                    >
                                      👑
                                    </span>
                                  )}
                                  {c.nickname ?? c.name ?? t('routes.anonymousClimber')}
                                </p>
                                {c.isFirstAscent && (
                                  <p className="text-xs font-medium text-primary">
                                    {t('routes.firstAscentLabel')}
                                  </p>
                                )}
                              </div>
                            </div>
                            {c.isFirstAscent && index < climbersData.climbers.length - 1 && (
                              <hr className="border-divider" />
                            )}
                          </Fragment>
                        ))}
                        {climbersData.private_count > 0 && (
                          <p className="mt-1 text-xs italic text-foreground/40">
                            {t('routes.privateAccountsNote', { count: climbersData.private_count })}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Sheet>

      {attemptsModalOpen && (
        <div
          className="fixed inset-0 z-[var(--z-dialog)] flex items-end justify-center bg-black/50 p-4 pb-8"
          onClick={() => {
            setAttemptsModalOpen(false)
            setAttempts(null)
            setRating(null)
            setComment('')
          }}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-background p-6"
            onClick={(e) => e.stopPropagation()}
          >
            {askAttempts && (
              <>
                <p className="mb-4 text-base font-semibold">{t('routes.attemptsLabel')}</p>
                <div className="mb-6 flex flex-wrap gap-2">
                  {ATTEMPT_OPTIONS.map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setAttempts(attempts === n ? null : n)}
                      className={`min-w-[2.75rem] rounded-xl border px-3 py-2 text-sm font-semibold transition-colors ${
                        attempts === n
                          ? 'border-primary bg-primary text-white'
                          : 'border-divider bg-content2 text-foreground hover:bg-content3'
                      }`}
                    >
                      {attemptLabel(n, t('routes.attemptsFlash'))}
                    </button>
                  ))}
                </div>
              </>
            )}
            {showRating && (
              <>
                <p className="mb-3 text-base font-semibold">{t('routes.ratingLabel')}</p>
                <div className={route.avg_rating == null ? 'mb-2' : 'mb-6'}>
                  <StarRating
                    value={rating}
                    onChange={setRating}
                    size="lg"
                    label={t('routes.ratingLabel')}
                    starLabel={(n) => t('routes.rateStars', { count: n })}
                  />
                </div>
                {route.avg_rating == null && (
                  <p className="mb-6 text-xs text-foreground/50">
                    {t('routes.ratingHiddenHint', { min: MIN_RATINGS_TO_SHOW })}
                  </p>
                )}
              </>
            )}
            {askComment && !route.has_ascent && (
              <div className="mb-6">
                <RouteCommentField value={comment} onValueChange={setComment} />
              </div>
            )}
            <Button
              className="w-full"
              color="primary"
              variant="solid"
              isLoading={saving}
              onClick={handleLog}
            >
              {t('routes.logAscent')}
            </Button>
            {saveError && <p className="mt-2 text-center text-sm text-danger">{saveError}</p>}
          </div>
        </div>
      )}

      {confirmLogAgainOpen && (
        <div
          className="fixed inset-0 z-[var(--z-dialog)] flex items-end justify-center bg-black/50 p-4 pb-8"
          onClick={() => setConfirmLogAgainOpen(false)}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-background p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="mb-2 text-base font-semibold">{t('routes.confirmLogAgainTitle')}</p>
            <p className="mb-6 text-sm text-foreground/60">{t('routes.confirmLogAgainBody')}</p>
            <div className="flex gap-2">
              <Button
                className="flex-1"
                color="default"
                variant="flat"
                onClick={() => setConfirmLogAgainOpen(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button
                className="flex-1"
                color="primary"
                variant="solid"
                isLoading={saving}
                onClick={() => {
                  setConfirmLogAgainOpen(false)
                  void handleLog()
                }}
              >
                {t('routes.confirm')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {confirmDeleteOpen &&
        (() => {
          const isFlash = route.attempts === 1
          const isFirstAscent =
            showClimbers &&
            (climbersData?.climbers.some((c) => c.userId === userId && c.isFirstAscent) ?? false)
          return (
            <div
              className="fixed inset-0 z-[var(--z-dialog)] flex items-end justify-center bg-black/50 p-4 pb-8"
              onClick={() => setConfirmDeleteOpen(false)}
            >
              <div
                className="w-full max-w-sm rounded-2xl bg-background p-6"
                onClick={(e) => e.stopPropagation()}
              >
                <p className="mb-3 text-base font-semibold">{t('routes.confirmDeleteTitle')}</p>
                <ul className="mb-4 space-y-1.5 text-sm text-foreground/70">
                  <li>{t('routes.confirmDeleteBody')}</li>
                  {isFlash && (
                    <li className="text-warning-700">{t('routes.confirmDeleteFlash')}</li>
                  )}
                  {isFirstAscent && (
                    <li className="text-primary">{t('routes.confirmDeleteFirstAscent')}</li>
                  )}
                </ul>
                <p className="mb-6 text-xs text-foreground/40">
                  {t('routes.confirmDeleteWarning')}
                </p>
                {saveError && <p className="mb-2 text-center text-sm text-danger">{saveError}</p>}
                <div className="flex gap-2">
                  <Button
                    className="flex-1"
                    color="default"
                    variant="flat"
                    onClick={() => setConfirmDeleteOpen(false)}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    className="flex-1"
                    color="danger"
                    variant="solid"
                    isLoading={saving}
                    onClick={() => {
                      setConfirmDeleteOpen(false)
                      void handleRemove()
                    }}
                  >
                    {t('routes.confirmDeleteButton')}
                  </Button>
                </div>
              </div>
            </div>
          )
        })()}

      {editAttemptsOpen && (
        <div
          className="fixed inset-0 z-[var(--z-dialog)] flex items-end justify-center bg-black/50 p-4 pb-8"
          onClick={() => {
            setEditAttemptsOpen(false)
            setPendingEditAttempts(null)
            setPendingEditRating(null)
            setPendingEditComment('')
          }}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-background p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="mb-4 text-base font-semibold">
              {showRating ? t('routes.editAscentTitle') : t('routes.editAttemptsTitle')}
            </p>
            {askAttempts && (
              <>
                <p className="mb-2 text-sm text-foreground/60">{t('routes.attemptsLabel')}</p>
                <div className="mb-6 flex flex-wrap gap-2">
                  {ATTEMPT_OPTIONS.map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setPendingEditAttempts(pendingEditAttempts === n ? null : n)}
                      className={`min-w-[2.75rem] rounded-xl border px-3 py-2 text-sm font-semibold transition-colors ${
                        pendingEditAttempts === n
                          ? 'border-primary bg-primary text-white'
                          : 'border-divider bg-content2 text-foreground hover:bg-content3'
                      }`}
                    >
                      {attemptLabel(n, t('routes.attemptsFlash'))}
                    </button>
                  ))}
                </div>
              </>
            )}
            {showRating && (
              <>
                <p className="mb-2 text-sm text-foreground/60">{t('routes.ratingLabel')}</p>
                <div className="mb-6">
                  <StarRating
                    value={pendingEditRating}
                    onChange={setPendingEditRating}
                    size="lg"
                    label={t('routes.ratingLabel')}
                    starLabel={(n) => t('routes.rateStars', { count: n })}
                  />
                </div>
              </>
            )}
            {askComment && (
              <div className="mb-6">
                <RouteCommentField
                  value={pendingEditComment}
                  onValueChange={(value) => {
                    editCommentTouched.current = true
                    setPendingEditComment(value)
                  }}
                />
              </div>
            )}
            <Button
              className="w-full"
              color="primary"
              variant="solid"
              isLoading={saving}
              onClick={() => void handleUpdateAscentDetails()}
            >
              {showRating ? t('routes.saveAscent') : t('routes.saveAttempts')}
            </Button>
            {saveError && <p className="mt-2 text-center text-sm text-danger">{saveError}</p>}
          </div>
        </div>
      )}

      {/* The same full-screen viewer as news photos: ✕, tap the dark, flick, or Escape. */}
      <ImageViewer
        src={zoomOpen && route.image_r2_key ? `${apiUrl}/climbing-routes/${route.id}/image` : null}
        alt={graded ? t('routes.gradeBadge', { grade: route.grade }) : ''}
        onClose={() => setZoomOpen(false)}
      />
    </>
  )
}
